import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { planRegistry, type Plan, type PlanId } from "@/data/meal-plan";
import {
  fetchMe,
  login as apiLogin,
  signup as apiSignup,
  logout as apiLogout,
  updatePreferredStore,
  savePlan,
  type AuthUser,
} from "@/lib/api";
import { DEFAULT_STORE, type StoreId } from "@/lib/stores";
import { flushPendingSaves, setSessionEnding } from "@/lib/pending-saves";
import type { RegimenPresets } from "@/types/meal-plan-state";

interface AuthContextValue {
  user: AuthUser | null;
  plan: Plan | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, password: string, planId: string) => Promise<void>;
  /**
   * Save pending edits, then end the session. Rejects with
   * `UnsavedChangesOnLogoutError` (still logged in) when the edits can't be
   * saved; `{ discardUnsaved: true }` then logs out anyway, dropping them.
   */
  logout: (options?: { discardUnsaved?: boolean }) => Promise<void>;
  // Supermarket the Compras tab builds carts in (Wong by default).
  preferredStore: StoreId;
  setPreferredStore: (store: StoreId) => Promise<void>;
  // Switch nutrition plan server-side; returns the weekday presets as seen
  // under the new plan. Use the week plan context's `switchPlan`, which saves
  // pending edits first and reloads the week.
  setPlan: (planId: PlanId) => Promise<RegimenPresets>;
}

/** Logout stopped because pending edits couldn't be saved (offline, server error). */
export class UnsavedChangesOnLogoutError extends Error {
  constructor(cause: unknown) {
    super("Hay cambios sin guardar", { cause });
    this.name = "UnsavedChangesOnLogoutError";
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const plan = user ? planRegistry[user.planId as PlanId] ?? null : null;

  // Check existing session on mount
  useEffect(() => {
    fetchMe()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setError(null);
    try {
      const u = await apiLogin(username, password);
      setSessionEnding(false);
      setUser(u);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error al iniciar sesión";
      setError(msg);
      throw e;
    }
  }, []);

  const signup = useCallback(async (username: string, password: string, planId: string) => {
    setError(null);
    try {
      const u = await apiSignup(username, password, planId);
      setSessionEnding(false);
      setUser(u);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error al crear cuenta";
      setError(msg);
      throw e;
    }
  }, []);

  const logoutFn = useCallback(async (options?: { discardUnsaved?: boolean }) => {
    setError(null);
    setSessionEnding(true);
    try {
      if (!options?.discardUnsaved) {
        try {
          await flushPendingSaves();
        } catch (e) {
          throw new UnsavedChangesOnLogoutError(e);
        }
      }
      await apiLogout();
      setUser(null);
    } catch (e) {
      setSessionEnding(false);
      const msg = e instanceof Error ? e.message : "Error al cerrar sesión";
      setError(msg);
      throw e;
    }
  }, []);

  const setPreferredStore = useCallback(async (store: StoreId) => {
    const saved = await updatePreferredStore(store);
    setUser((prev) => (prev ? { ...prev, preferredStore: saved } : prev));
  }, []);

  const setPlan = useCallback(async (planId: PlanId) => {
    const saved = await savePlan(planId);
    setUser((prev) => (prev ? { ...prev, planId: saved.planId } : prev));
    return saved.presets;
  }, []);

  const preferredStore = user?.preferredStore ?? DEFAULT_STORE;

  return (
    <AuthContext.Provider
      value={{ user, plan, loading, error, login, signup, logout: logoutFn, preferredStore, setPreferredStore, setPlan }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
